import { Box, Typography } from "@material-ui/core";
import { useMutation } from "@tanstack/react-query";
import GenericButton from "components/Buttons/GenericButton";
import GenericDialog from "components/Dialogs/GenericDialogV2";
import FormDropdown from "components/FormFields/FormDropdown";
import FormInputV2 from "components/FormFields/FormInputV2";
import { inactiveWorkCenterEmployee } from "crud/workCentersEmployer.crud";
import { ConditionGuard } from "guards/ConditionGuard";
import { useDataset } from "hooks/useDataset";
import useMessage from "hooks/useMessage";
import { useMemo } from "react";
import { useForm } from "react-hook-form";
import { useDispatch } from "react-redux";
import { useParams } from "react-router-dom/cjs/react-router-dom.min";
import { showGenericTwoButtonDialogAction } from "redux/actions/dialogsAction";
import { DATASET } from "utils/const";

const getApiErrorMessage = (error, fallback) => {
  if (error?.response?.data?.errors) {
    return Object.values(error.response.data.errors).flat().join(". ");
  }
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  return fallback;
};

const InactiveEmployeeModal = ({ open, handleClose, selectedWorkCenterEmployer, handleCloseDialog, onSaveSuccess }) => {
  const { control, handleSubmit, watch } = useForm();

  const { id } = useParams();
  const { showSuccessMessage, showErrorMessage } = useMessage();

  const dispatch = useDispatch();

  const showDialog = (message, submessage, yesAction, noAction) => {
    dispatch(showGenericTwoButtonDialogAction(message, submessage, yesAction, noAction));
  };

  const { data: INACTIVE_EMPLOYEE_MOTIVES } = useDataset({
    name: DATASET.INACTIVE_EMPLOYEE_MOTIVES,
  });

  const { mutateAsync: inactiveWorkCenterEmployeeMutation, isLoading: inactiveWorkCenterEmployeeLoading } = useMutation(
    ["inactiveEmployee", id],
    (data) => inactiveWorkCenterEmployee(data),
    {
      onSuccess: () => {
        showSuccessMessage("Trabalhador inactivado com sucesso.");

        onSaveSuccess?.();
        handleClose();
        handleCloseDialog();
      },

      onError: (error) => {
        const errorMessage = getApiErrorMessage(error, "Ocorreu um erro ao inactivar o trabalhador!");
        showErrorMessage(errorMessage);
      },
    }
  );

  async function onSubmit(data) {
    const inactiveData = {
      id: selectedWorkCenterEmployer,
      motiveId: data.motiveId,
      otherMotiveDescription: data.otherMotiveDescription,
    };

    showDialog(
      "Inactivar Trabalhador",
      "Tem a certeza que deseja inactivar este trabalhador?",
      async () => {
        await inactiveWorkCenterEmployeeMutation(inactiveData);
        handleCloseDialog();
      },
      () => {}
    );
  }

  const showObservationInput = useMemo(() => {
    return INACTIVE_EMPLOYEE_MOTIVES?.find((motive) => motive.keyValue === "OTHER")?.code === watch("motiveId");
  }, [INACTIVE_EMPLOYEE_MOTIVES, watch("motiveId")]);

  return (
    <GenericDialog open={open} onClose={handleClose} fullWidth={true} maxWidth="sm">
      <Typography
        style={{
          fontSize: "1.6rem",
          marginBottom: "2rem",
        }}
        variant="h2"
        color="primary"
      >
        Inactivar Trabalhador
      </Typography>

      <form>
        <FormDropdown control={control} name="motiveId" label="Motivo da Saída" options={INACTIVE_EMPLOYEE_MOTIVES} />

        <ConditionGuard condition={showObservationInput}>
          <FormInputV2 control={control} name="otherMotiveDescription" label="Descrição" isMultiLine rows={4} />
        </ConditionGuard>

        <Box display="flex" justifyContent="flex-end" gridGap={12}>
          <GenericButton color="tertiary" onClick={handleClose}>
            Cancelar
          </GenericButton>
          <GenericButton onClick={handleSubmit(onSubmit)} color="primary" loading={inactiveWorkCenterEmployeeLoading}>
            Submeter
          </GenericButton>
        </Box>
      </form>
    </GenericDialog>
  );
};

export default InactiveEmployeeModal;
